import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';

export type PushSdkState = 'disabled' | 'idle' | 'loading' | 'ready' | 'failed';

export interface PushNotificationHealth {
  sdkState: PushSdkState;
  sdkFailureReason: string | null;
  configuredAppId: string;
  online: boolean;
  secureContext: boolean;
  browserSupported: boolean;
  browserPermission: NotificationPermission | 'unsupported';
  subscribed: boolean | null;
  subscriptionId: string | null;
  sdkExternalUserId: string | null;
  serviceWorkerRegistered: boolean | null;
  externalUserId: string | null;
  identityApplied: boolean;
}

@Injectable({ providedIn: 'root' })
export class SignalService {
  private readonly isEnabled = environment.production;
  private readonly appId = 'ae3776e3-ea29-498d-847a-89696b39c3a7';
  private readonly sdkLoadTimeoutMs = 10000;
  private readonly sdkReadyTimeoutMs = 3000;
  private readonly identityOperationTimeoutMs = 3000;
  private sdkLoadPromise: Promise<boolean> | null = null;
  private initPromise: Promise<any> | null = null;
  private externalUserId: string | null = null;
  private identityApplied = false;
  private identityListenersRegistered = false;
  private identityOperation: Promise<void> = Promise.resolve();
  private sdkState: PushSdkState = this.isEnabled ? 'idle' : 'disabled';
  private sdkFailureReason: string | null = null;

  private hasWindow(): boolean {
    return typeof window !== 'undefined';
  }

  private loadSdkDeferred(): Promise<boolean> {
    if (!this.isEnabled || !this.hasWindow()) {
      return Promise.resolve(false);
    }
    if (this.sdkLoadPromise) {
      return this.sdkLoadPromise;
    }

    this.sdkState = 'loading';
    this.sdkLoadPromise = new Promise((resolve) => {
      let settled = false;
      let timeoutHandle: number | null = null;
      const finish = (loaded: boolean, failureReason: string | null = null) => {
        if (settled) {
          return;
        }
        settled = true;
        if (timeoutHandle !== null) {
          window.clearTimeout(timeoutHandle);
        }
        if (!loaded) {
          this.markSdkFailed(failureReason || 'load-failed');
        }
        resolve(loaded);
      };

      timeoutHandle = window.setTimeout(
        () => finish(false, 'load-timeout'),
        this.sdkLoadTimeoutMs
      );

      const existingScript = document.querySelector<HTMLScriptElement>('script[data-onesignal-sdk]');
      if (existingScript) {
        const loadState = existingScript.getAttribute('data-onesignal-load-state');
        if (loadState === 'loaded') {
          finish(true);
          return;
        }
        if (loadState === 'failed') {
          finish(false, 'load-error');
          return;
        }
        existingScript.addEventListener('load', () => finish(true), { once: true });
        existingScript.addEventListener('error', () => finish(false, 'load-error'), { once: true });
        return;
      }

      const startLoad = () => {
        const script = document.createElement('script');
        script.src = 'https://cdn.onesignal.com/sdks/OneSignalSDK.js';
        script.async = true;
        script.setAttribute('data-onesignal-sdk', 'true');
        script.onload = () => {
          script.setAttribute('data-onesignal-load-state', 'loaded');
          finish(true);
        };
        script.onerror = () => {
          script.setAttribute('data-onesignal-load-state', 'failed');
          finish(false, 'load-error');
        };
        document.head.appendChild(script);
      };

      const win = window as any;
      if (typeof win.requestIdleCallback === 'function') {
        win.requestIdleCallback(startLoad, { timeout: 3000 });
      } else {
        window.setTimeout(startLoad, 2000);
      }
    });
    return this.sdkLoadPromise;
  }

  async onLoad(): Promise<any> {
    if (!this.isEnabled || !this.hasWindow()) {
      return null;
    }

    const win = window as any;
    win.OneSignal = win.OneSignal || [];
    const loaded = await this.loadSdkDeferred();
    if (!loaded) {
      return null;
    }

    const sdkReady = new Promise<any>((resolve) => {
      win.OneSignal.push(() => resolve(win.OneSignal));
    });
    return this.settleWithin(sdkReady, this.sdkReadyTimeoutMs, 'ready-timeout')
      .catch((error) => {
        this.markSdkFailed('ready-timeout', error);
        return null;
      });
  }

  onInit(): void {
    if (this.isEnabled && this.hasWindow()) {
      void this.ensureInitialized();
    }
  }

  private ensureInitialized(): Promise<any> {
    if (!this.isEnabled || !this.hasWindow()) {
      return Promise.resolve(null);
    }
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.onLoad().then(async (OneSignal) => {
      if (!OneSignal) {
        return null;
      }

      await Promise.resolve(OneSignal.init({
        appId: this.appId,
        autoResubscribe: true,
      }));
      OneSignal.on('notificationDisplay', (event: any) => {
        console.warn('OneSignal notification displayed:', event);
      });
      this.registerIdentityListeners(OneSignal);
      this.sdkState = 'ready';
      return OneSignal;
    }).catch((error) => {
      this.markSdkFailed('initialization-error', error);
      return null;
    });
    return this.initPromise;
  }

  setExternalId(id: string): Promise<void> {
    if (!this.isEnabled || !this.hasWindow()) {
      return Promise.resolve();
    }
    const normalizedId = id?.trim();
    if (!normalizedId) {
      return Promise.resolve();
    }

    this.externalUserId = normalizedId;
    this.identityApplied = false;
    return this.applyExternalId(normalizedId);
  }

  removeExternalId(maxWaitMs?: number): Promise<void> {
    if (!this.isEnabled || !this.hasWindow()) {
      return Promise.resolve();
    }

    this.externalUserId = null;
    this.identityApplied = false;
    const removal = this.queueIdentityOperation(
      (OneSignal) => OneSignal.removeExternalUserId(),
      'remove-external-id'
    );
    return maxWaitMs && maxWaitMs > 0
      ? this.settleWithin(removal, maxWaitMs, 'logout-wait-timeout', undefined, true)
      : removal;
  }

  async getPushHealth(): Promise<PushNotificationHealth> {
    const OneSignal = await this.ensureInitialized();
    const [subscribed, subscriptionId, sdkExternalUserId, serviceWorkerRegistered] = await Promise.all([
      OneSignal ? this.getSubscriptionState(OneSignal) : Promise.resolve(null),
      OneSignal ? this.getLegacySdkValue<string>(OneSignal, 'getUserId') : Promise.resolve(null),
      OneSignal ? this.getLegacySdkValue<string>(OneSignal, 'getExternalUserId') : Promise.resolve(null),
      this.hasOneSignalServiceWorker(),
    ]);
    return {
      sdkState: this.sdkState,
      sdkFailureReason: this.sdkFailureReason,
      configuredAppId: this.appId,
      online: typeof navigator === 'undefined' ? false : navigator.onLine,
      secureContext: this.hasWindow() && window.isSecureContext,
      browserSupported: this.isBrowserPushSupported(),
      browserPermission: this.getBrowserPermission(),
      subscribed,
      subscriptionId,
      sdkExternalUserId,
      serviceWorkerRegistered,
      externalUserId: this.externalUserId,
      identityApplied: this.identityApplied,
    };
  }

  private registerIdentityListeners(OneSignal: any): void {
    if (this.identityListenersRegistered || typeof OneSignal?.on !== 'function') {
      return;
    }
    this.identityListenersRegistered = true;

    const restoreIdentity = () => {
      if (this.externalUserId) {
        void this.applyExternalId(this.externalUserId);
      }
    };
    OneSignal.on('subscriptionChange', (isSubscribed: boolean) => {
      if (isSubscribed) {
        restoreIdentity();
      }
    });
    OneSignal.on('notificationPermissionChange', (change: any) => {
      if (change?.to === 'granted') {
        restoreIdentity();
      }
    });
  }

  private queueIdentityOperation(
    operation: (OneSignal: any) => Promise<unknown> | unknown,
    operationName: string
  ): Promise<void> {
    const run = async () => {
      const OneSignal = await this.ensureInitialized();
      if (!OneSignal) {
        return;
      }
      await this.settleWithin(
        Promise.resolve().then(() => operation(OneSignal)),
        this.identityOperationTimeoutMs,
        `${operationName}-timeout`
      ).catch((error) => console.warn(`OneSignal ${operationName} failed.`, error));
    };

    this.identityOperation = this.identityOperation.then(run, run);
    return this.identityOperation;
  }

  private applyExternalId(externalUserId: string): Promise<void> {
    return this.queueIdentityOperation(async (OneSignal) => {
      await Promise.resolve(OneSignal.setExternalUserId(externalUserId));
      if (this.externalUserId === externalUserId) {
        this.identityApplied = true;
      }
    }, 'set-external-id');
  }

  private getBrowserPermission(): NotificationPermission | 'unsupported' {
    return !this.hasWindow() || typeof Notification === 'undefined'
      ? 'unsupported'
      : Notification.permission;
  }

  private getSubscriptionState(OneSignal: any): Promise<boolean | null> {
    if (typeof OneSignal?.isPushNotificationsEnabled !== 'function') {
      return Promise.resolve(null);
    }
    const subscriptionState = new Promise<boolean>((resolve, reject) => {
      let callbackSettled = false;
      const settle = (enabled: boolean) => {
        if (!callbackSettled) {
          callbackSettled = true;
          resolve(enabled);
        }
      };
      try {
        const result = OneSignal.isPushNotificationsEnabled(settle);
        if (result && typeof result.then === 'function') {
          result.then(settle, reject);
        }
      } catch (error) {
        reject(error);
      }
    });
    return this.settleWithin(
      subscriptionState,
      this.identityOperationTimeoutMs,
      'subscription-state-timeout',
      null,
      true
    );
  }

  private getLegacySdkValue<T>(OneSignal: any, methodName: string): Promise<T | null> {
    if (typeof OneSignal?.[methodName] !== 'function') {
      return Promise.resolve(null);
    }
    const sdkValue = new Promise<T>((resolve, reject) => {
      let settled = false;
      const settle = (value: T) => {
        if (!settled) {
          settled = true;
          resolve(value);
        }
      };
      try {
        const result = OneSignal[methodName](settle);
        if (result && typeof result.then === 'function') {
          result.then(settle, reject);
        } else if (result !== undefined) {
          settle(result);
        }
      } catch (error) {
        reject(error);
      }
    });
    return this.settleWithin(
      sdkValue,
      this.identityOperationTimeoutMs,
      `${methodName}-timeout`,
      null,
      true
    );
  }

  private isBrowserPushSupported(): boolean {
    return this.hasWindow()
      && typeof Notification !== 'undefined'
      && typeof navigator !== 'undefined'
      && 'serviceWorker' in navigator
      && 'PushManager' in window;
  }

  private async hasOneSignalServiceWorker(): Promise<boolean | null> {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker?.getRegistrations) {
      return null;
    }
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      return registrations.some((registration) =>
        [registration.active, registration.waiting, registration.installing]
          .some((worker) => worker?.scriptURL?.includes('OneSignalSDKWorker.js'))
      );
    } catch (error) {
      console.warn('Unable to inspect OneSignal service worker registration.', error);
      return null;
    }
  }

  private settleWithin<T>(
    operation: Promise<T>,
    timeoutMs: number,
    timeoutReason?: string,
    timeoutValue?: T,
    resolveOnTimeout = false
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      let settled = false;
      const timeoutHandle = window.setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        resolveOnTimeout
          ? resolve(timeoutValue as T)
          : reject(new Error(timeoutReason || 'operation-timeout'));
      }, timeoutMs);

      operation.then(
        (value) => {
          if (!settled) {
            settled = true;
            window.clearTimeout(timeoutHandle);
            resolve(value);
          }
        },
        (error) => {
          if (!settled) {
            settled = true;
            window.clearTimeout(timeoutHandle);
            reject(error);
          }
        }
      );
    });
  }

  private markSdkFailed(reason: string, error?: unknown): void {
    this.sdkState = 'failed';
    this.sdkFailureReason = reason;
    console.warn(`OneSignal SDK ${reason}.`, error || '');
  }
}
